/**
 * Módulo Administração — o que só quem cuida do sistema vê.
 *
 * Criado a pedido do Lucas (04/08), e a razão dele é a que importa: o
 * Escritório continua dando acesso a quem trabalha no dia a dia, mas
 * **cadastrar e corrigir saldo saem de lá**. São os dois poderes que mudam a
 * base do sistema em vez de operar sobre ela.
 *
 * O que mora aqui:
 *   PANORAMA — erros abertos, ações de hoje, quem tem acesso.
 *   LOG      — tudo que foi feito e por quem, com busca.
 *   ERROS    — o que os usuários encontraram, reportado ou não.
 *   FROTA    — veículos, abastecimentos e manutenções.
 *   AVISOS   — a central do que o sistema notifica, e quando.
 *
 * A proteção de verdade está no servidor (`SO_MASTER` em `auth/plugin.ts`).
 * Esta tela não decide acesso — ela só não mostra o que a pessoa não pode
 * fazer, o que é conveniência, não segurança.
 */

import { api, ErroApi } from '../../api.js';
import { escapar } from '../../comum/formato.js';
import { montarMenuLateral } from '../../comum/menu-lateral.js';
import { montarLog } from './log.js';
import { montarErros } from './erros.js';
import { montarFrota } from './frota.js';
import { montarCadastroObras } from '../cadastros/obras.js';
import { montarCadastroPessoas } from '../cadastros/pessoas.js';
import { montarAvisos } from './avisos.js';
import { montarRastreamento } from './rastreamento.js';
import { montarVigia } from './vigia.js';
import { montarCaixa } from './caixa.js';

interface Resumo {
  errosAbertos: number;
  errosReportados: number;
  acoesHoje: number;
  acoesTotal: number;
  pessoasComAcesso: number;
  administradores: number;
  /** Abastecimentos enviados "mesmo assim" com inconsistência (08/09). */
  abastecimentosEmRevisao: number;
  vigiaAlertas?: number;
  /** Documentos da caixa de entrada esperando conferência (27/10). */
  caixaPendentes?: number;
}

export function montarMaster(raiz: HTMLElement): void {
  let resumo: Resumo | undefined;

  raiz.innerHTML = `
    <div class="modulo-com-menu">
      <aside id="menu-master"></aside>
      <div id="area-master"><p class="carregando">Carregando…</p></div>
    </div>`;
  const area = raiz.querySelector<HTMLElement>('#area-master')!;

  const itensDoMenu = () => [
    { chave: 'painel', nome: 'Panorama', emoji: '📊' },
    { chave: 'log', nome: 'Log do sistema', emoji: '📔' },
    { chave: 'erros', nome: 'Erros', emoji: '🚨', badge: resumo?.errosAbertos ?? 0 },
    { chave: 'caixa', nome: 'Caixa de entrada', emoji: '📨', badge: resumo?.caixaPendentes ?? 0 },
    { chave: 'frota', nome: 'Frota', emoji: '🚛', badge: resumo?.abastecimentosEmRevisao ?? 0 },
    { chave: 'rastreamento', nome: 'Rastreamento', emoji: '🛰️' },
    { chave: 'avisos', nome: 'Avisos', emoji: '🔔' },
    { chave: 'vigia', nome: 'Vigia', emoji: '🩺', badge: resumo?.vigiaAlertas ?? 0 },
    {
      chave: 'cadastros', nome: 'Cadastros', emoji: '🗂️', separaAntes: true,
      filhos: [
        { chave: 'cad-obras', nome: 'Obras' },
        { chave: 'cad-pessoas', nome: 'Pessoas' },
      ],
    },
  ];

  const menu = montarMenuLateral(raiz.querySelector<HTMLElement>('#menu-master')!, itensDoMenu(), (chave) => {
    abrir(chave);
  });

  function abrir(chave: string) {
    // Clicar no pai "Cadastros" abre a primeira tela dele — pai não é beco.
    const efetiva = chave === 'cadastros' ? 'cad-obras' : chave;
    menu.marcar(efetiva);
    const chaveAberta = efetiva;
    if (chaveAberta === 'log') return montarLog(area);
    if (chaveAberta === 'erros') return montarErros(area, () => void recarregarResumo());
    if (chaveAberta === 'cad-obras') return montarCadastroObras(area);
    if (chaveAberta === 'cad-pessoas') return montarCadastroPessoas(area);
    if (chaveAberta === 'frota') return montarFrota(area);
    if (chaveAberta === 'frota-revisao') {
      menu.marcar('frota');
      return montarFrota(area, 'revalidacao');
    }
    if (chaveAberta === 'rastreamento') return montarRastreamento(area);
    if (chaveAberta === 'avisos') return montarAvisos(area);
    if (chaveAberta === 'vigia') return montarVigia(area);
    if (chaveAberta === 'caixa') return montarCaixa(area, () => void recarregarResumo());
    desenharPainel();
  }

  function desenharPainel() {
    if (!resumo) return;
    area.innerHTML = `
      ${
        (resumo.caixaPendentes ?? 0) > 0
          ? `<section class="ped-painel painel-revisao">
               <h3>📨 ${resumo.caixaPendentes} documento(s) da caixa de entrada para conferir</h3>
               <p class="dica">Chegaram por e-mail em sistema@ademaldo.com.br e o sistema ficou com dúvida: precisa de um olhar humano.</p>
               <div class="conf-acoes"><button class="btn" id="ir-caixa">Abrir a caixa de entrada</button></div>
             </section>`
          : ''
      }
      ${
        resumo.abastecimentosEmRevisao > 0
          ? `<section class="ped-painel painel-revisao">
               <h3>⛽ ${resumo.abastecimentosEmRevisao} abastecimento(s) enviados com inconsistência</h3>
               <p class="dica">O motorista mandou "mesmo assim" — ticket, nota ou placa não batiam. Precisa de um olhar humano.</p>
               <div class="conf-acoes"><button class="btn" id="ir-revisao">Abrir a revisão</button></div>
             </section>`
          : ''
      }
      <div class="kpis">
        ${kpi('Erros em aberto', resumo.errosAbertos, resumo.errosAbertos > 0 ? 'alerta' : '')}
        ${kpi('Reportados por usuários', resumo.errosReportados, resumo.errosReportados > 0 ? 'aviso' : '')}
        ${kpi('Ações hoje', resumo.acoesHoje, '')}
        ${kpi('Ações registradas', resumo.acoesTotal, '')}
        ${kpi('Pessoas com acesso', resumo.pessoasComAcesso, '')}
        ${kpi('Administradores', resumo.administradores, '')}
      </div>

      <section class="ped-painel">
        <h3>O que é esta área</h3>
        <p class="dica">
          Aqui ficam o <b>log de tudo que foi feito</b>, os <b>erros</b> que os usuários
          encontraram e a <b>correção de saldo</b> — a única operação que muda um número sem
          que nada tenha entrado ou saído. Cadastrar itens, obras e pessoas é do Escritório.
        </p>
        <p class="dica">
          O <b>log</b> registra sozinho toda ação que altera algo, com quem fez. Não depende de
          nenhuma tela lembrar de anotar — por isso não tem buraco.
        </p>
      </section>`;
    area.querySelector('#ir-revisao')?.addEventListener('click', () => abrir('frota-revisao'));
    area.querySelector('#ir-caixa')?.addEventListener('click', () => abrir('caixa'));
  }

  async function recarregarResumo() {
    try {
      resumo = await api.get<Resumo>('/api/master/resumo');
      menu.reabastecer(itensDoMenu());
    } catch {
      // O painel some, o resto do módulo continua servindo.
    }
  }

  void (async () => {
    try {
      resumo = await api.get<Resumo>('/api/master/resumo');
    } catch (erro) {
      area.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar.',
      )}</p>`;
      return;
    }
    menu.reabastecer(itensDoMenu());
    abrir('painel');
  })();
}

function kpi(rotulo: string, valor: number, tom: string): string {
  return `<div class="kpi ${tom}"><span class="kpi-rotulo">${escapar(rotulo)}</span><span class="kpi-valor">${valor}</span></div>`;
}
